import React, { useState } from 'react';
import { CheckCircle2, Lock } from 'lucide-react';
import { setPassword, validatePasswordStrength } from '../../Backend/services/authService';
import AuthButton from './AuthButton';
import AuthField from './AuthField';
import PasswordRequirements from './PasswordRequirements';
import styles from './GooglePasswordSetup.module.css';

interface GooglePasswordSetupProps {
  email?: string;
  onDone: () => void;
  onSkip: () => void;
}

/**
 * Passo curto apresentado depois do primeiro login Google.
 * A sessão já está criada; este passo apenas acrescenta o segundo método de
 * entrada para a mesma conta, sem obrigar a pessoa a procurar a segurança do
 * perfil ou a atualizar a página.
 */
const GooglePasswordSetup: React.FC<GooglePasswordSetupProps> = ({ email, onDone, onSkip }) => {
  const [password, setPasswordValue] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const validation = password ? validatePasswordStrength(password) : null;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (!validation?.valid) {
      setError(validation?.errors[0] || 'Escolhe uma password mais forte.');
      return;
    }

    if (password !== confirmPassword) {
      setError('As passwords não coincidem.');
      return;
    }

    setIsSubmitting(true);
    try {
      await setPassword(password, confirmPassword);
      onDone();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Não foi possível guardar a password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} role="presentation">
      <section
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="google-password-title"
      >
        <div className={styles.icon} aria-hidden="true"><Lock size={22} /></div>
        <p className={styles.eyebrow}>Conta ligada com sucesso</p>
        <h2 id="google-password-title">Falta só uma coisa</h2>
        <p className={styles.description}>
          Entraste com Google. Define uma password para também poderes entrar rapidamente com email e password.
        </p>
        {email && <p className={styles.email}>{email}</p>}

        {error && <div className={styles.error} role="alert">{error}</div>}

        <form onSubmit={submit} className={styles.form}>
          <AuthField
            label="Nova password"
            reveal
            value={password}
            onChange={(event) => setPasswordValue(event.target.value)}
            placeholder="Mínimo 8 caracteres"
            autoComplete="new-password"
            autoFocus
          />
          <AuthField
            label="Confirmar password"
            reveal
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            placeholder="Repete a password"
            autoComplete="new-password"
          />

          {password && validation && <PasswordRequirements validation={validation} />}

          <AuthButton type="submit" fullWidth loading={isSubmitting} className={styles.submit}>
            Ativar entrada com password
          </AuthButton>
        </form>

        <button type="button" className={styles.skip} onClick={onSkip} disabled={isSubmitting}>
          Agora não, continuar com Google
        </button>
        <p className={styles.trust}><CheckCircle2 size={15} /> Podes alterar isto depois em Perfil · Segurança.</p>
      </section>
    </div>
  );
};

export default GooglePasswordSetup;
